// Background-cache a complete song for offline use.
//
// Used by both the editor (own songs) and the share view (shared
// songs). Splits caching into "data" (song JSON + images, always)
// and "audio" (attachment MP3s, opt-in).
//
// Everything runs in the background and never blocks the UI.

import { supabase } from '../supabase'
import { CACHE_IMAGES, CACHE_AUDIO } from '../../config/offline'
import { cacheBlob, deleteBlob } from './blobCache'
import {
  saveSongOffline,
  setAttachmentPrefs,
  removeSongOffline,
} from './songCache'
import { requestPersistence } from './quota'
import type { Song, SongAttachment, SongElement } from '../../types/songbook'

// Lossless formats (wav, flac) are deliberately excluded: too large
// to make sense as rehearsal-room offline caches.
const AUDIO_RE = /\.(mp3|ogg|aac|m4a|opus|webm)$/i

export function getAudioAttachments(song: Song): SongAttachment[] {
  return song.attachments.filter((a) => AUDIO_RE.test(a.filename))
}

export function getAudioBytes(song: Song): number {
  return getAudioAttachments(song).reduce((sum, a) => sum + a.size, 0)
}

/**
 * Rough byte estimate for "what would this song occupy offline":
 * - Song JSON length (covers elements, MIDI, info, notes, ...)
 * - Sum of audio attachment sizes
 * Image element bytes are not included because their sizes are not
 * tracked in the song record.
 */
export function estimateSongOfflineBytes(song: Song): number {
  let bytes = JSON.stringify(song).length
  bytes += getAudioBytes(song)
  return bytes
}

function getImageElements(song: Song): Array<{ storagePath: string }> {
  return song.elements
    .filter((e: SongElement): e is SongElement & { type: 'image'; storagePath: string } =>
      e.type === 'image',
    )
    .map((e) => ({ storagePath: e.storagePath }))
}

/**
 * Cache the song JSON and every image element. Audio attachments are
 * left untouched — call cacheSongAudio() separately if the user
 * opted in.
 *
 * Safe to call multiple times: idempotent at the IDB level and
 * Cache API level.
 */
export async function cacheSongData(
  song: Song,
  source: 'own' | 'shared',
  shareToken?: string | null,
): Promise<void> {
  await saveSongOffline(song, source, shareToken ?? null)

  for (const img of getImageElements(song)) {
    await fetchAndCache(img.storagePath, CACHE_IMAGES)
  }
}

/**
 * Cache every audio attachment of the song into CACHE_AUDIO.
 * Persists the user's choice via setAttachmentPrefs(true) so we
 * don't ask again on later visits.
 */
export async function cacheSongAudio(song: Song): Promise<void> {
  await requestPersistence()
  await setAttachmentPrefs(song.id, true)

  for (const att of getAudioAttachments(song)) {
    await fetchAndCache(att.storagePath, CACHE_AUDIO)
  }
}

/**
 * Mark "user said no, don't ask again" for this song's audio.
 */
export async function dismissAudioCache(songId: string): Promise<void> {
  await setAttachmentPrefs(songId, false)
}

/**
 * Remove every offline trace of a song:
 * - the IDB record (and the attachmentPrefs entry)
 * - cached image blobs for image elements
 * - cached audio blobs for audio attachments
 *
 * Note: the listenUrl track lives in the same audio cache but is keyed
 * by the original URL — we delete it here too if present so the user's
 * "free up space" intent works as expected.
 */
export async function removeSongFromOffline(song: Song): Promise<void> {
  for (const img of getImageElements(song)) {
    await deleteBlob(CACHE_IMAGES, img.storagePath)
  }
  for (const att of getAudioAttachments(song)) {
    await deleteBlob(CACHE_AUDIO, att.storagePath)
  }
  if (song.listenUrl) {
    try {
      const cache = await caches.open(CACHE_AUDIO)
      await cache.delete(song.listenUrl)
    } catch {
      // ignore — best-effort cleanup
    }
  }
  await removeSongOffline(song.id)
}

async function fetchAndCache(storagePath: string, cacheName: string): Promise<void> {
  try {
    const { data, error } = await supabase.storage
      .from('song-files')
      .createSignedUrl(storagePath, 300)

    if (error || !data?.signedUrl) {
      console.debug('[offline] signed URL failed for', storagePath)
      return
    }

    const res = await fetch(data.signedUrl)
    if (!res.ok) {
      console.debug('[offline] fetch failed', storagePath, res.status)
      return
    }

    const blob = await res.blob()
    await cacheBlob(cacheName, storagePath, blob)
  } catch (err) {
    console.debug('[offline] fetchAndCache exception:', err)
  }
}
